import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import QualitasMessage from "./QualitasMessage";



// Supabase
import supabase from "../../supabaseClient";

export default function UnsentDocuments() {
  const theme = useTheme();
  const [files, setFiles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const { t } = useTranslation();

  // Context
  const { showNotification } = useNotification();

  const columns = [
    { field: "type", headerName: t("qualitas.columns.type"), width: 200 },
    { field: "policy", headerName: t("qualitas.columns.policy"), width: 200 },
    { field: "name", headerName: t("qualitas.columns.name"), width: 200 },
    { field: "file", headerName: t("qualitas.columns.file"), width: 200 },
    {
      field: "issuedDate",
      headerName: t("qualitas.columns.issuedDate"),
      width: 200,
    },
    {
      field: "expirationDate",
      headerName: t("qualitas.columns.expirationDate"),
      width: 200,
    },
    {
      field: "conditions",
      headerName: t("qualitas.columns.conditions"),
      width: 200,
    },
  ];

  useEffect(() => {
    const fetchFiles = async () => {
      const { data: filesData, error: filesError } = await supabase
        .from("files")
        .select("*")
        .in("type", ["receipt", "policy"]);

      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select("file_id")
        .not("file_id", "is", null);

      if (filesError || messagesError) {
        showNotification(
          t("qualitas.fetchError", {
            error: filesError.message || messagesError.message,
          })
        );
      } else {
        // Extract file_ids from messages
        const messageFileIds = new Set(messagesData.map((msg) => msg.file_id));

        // Filter out files whose id exists in messages' file_id
        const filteredFiles = filesData.filter(
          (file) => !messageFileIds.has(file.id)
        );

        const transformedFiles = filteredFiles.map((file) => ({
          id: file.id,
          type:
            file.type === "policy"
              ? t("qualitas.policy")
              : t("qualitas.receipt"),
          file: file.path,
          name: file.metadata?.["policy_owner_name"] || "",
          policy: file.metadata?.["policy"] || "",
          issuedDate: file.metadata?.["issued_date"] || "",
          expirationDate: file.metadata?.["expiration_date"] || "",
          conditions: file.metadata?.["conditions_ref"] || "",
        }));
        setFiles(transformedFiles);
      }
    };

    fetchFiles();
  }, [modalOpen]);

  const handleRowClick = (params) => {
    trackEvent("qualitas-row_clicked");
    setSelectedFile(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    trackEvent("qualitas-modal_closed");
    setModalOpen(false);
    setSelectedFile(null);
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          rows={files}
          columns={columns}
          onRowClick={handleRowClick}
          disableSelectionOnClick
          autoHeight
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <QualitasMessage
        open={modalOpen}
        onClose={handleCloseModal}
        file={selectedFile}
      />
    </Box>
  );
}
